import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse,
    HttpStatusCode,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '@services/auth.service';
import { LOCAL_STORAGE_KEY, StorageService } from '@services/storage.service';
import { CommonStateService } from '@app/services/common.service';
import { ToasterService } from '@services/toaster.service';
import { TranslationService } from '@services/translation.service';
import { WalletService } from '@services/wallet.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    constructor(
        private readonly authService: AuthService,
        private readonly storageService: StorageService,
        private readonly commonService: CommonStateService,
        private readonly toasterService: ToasterService,
        private readonly translationService: TranslationService,
        private readonly walletService: WalletService
    ) {}

    /** Backend AI endpoints (/api/ai/*) that may spend credits. */
    private isAiRequest<T>(request: HttpRequest<T>): boolean {
        return /\/ai\//.test(request.url) && (request.method === 'POST' || request.method === 'PUT');
    }

    /**
     * Intercepts HTTP requests to add authorization headers and handle errors.
     * @param request The outgoing HTTP request.
     * @param next The next handler in the chain.
     * @returns An observable of the HTTP event.
     */
    intercept<T>(
        request: HttpRequest<T>,
        next: HttpHandler
    ): Observable<HttpEvent<T>> {
        // Super Admin API: SuperAdminService attaches its own token and handles its own 401/403.
        // Never add the tenant token or run tenant refresh/logout here (token-management Step 11).
        if (request.url.includes('/super-admin/')) {
            return next.handle(request);
        }

        const accessToken = this.storageService.getItem(
            LOCAL_STORAGE_KEY.ACCESS_TOKEN
        );

        let authorizationHeaders: string;
        if (request.headers.has('Skip-Authorization')) {
            const clonedRequest = request.clone({
                headers: request.headers.delete('Authorization'),
            });
            return next.handle(clonedRequest);
        }

        if (
            request.url.split('/').includes('verifyOTP') ||
            request.url.split('/').includes('resendotp')
        ) {
            authorizationHeaders = `Temp ${this.storageService.getItem(
                LOCAL_STORAGE_KEY.TEMP_TOKEN
            )}`;
        } else if (
            request.url.split('/').includes('login') ||
            request.url.split('/').includes('forgotPassword')
        ) {
            authorizationHeaders = '';
        } else {
            authorizationHeaders = `Bearer ${accessToken}`;
        }

        const clonedRequest = request.clone({
            setHeaders: {
                Authorization: authorizationHeaders,
            },
        });

        return next.handle(clonedRequest).pipe(
            catchError((error: HttpErrorResponse) => {
                if (!request.url.includes('/auth')) {
                    if (error.status === 401) {
                        return this.authService.refreshToken().pipe(
                            switchMap((newAccessToken) => {
                                const retryRequest = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newAccessToken.data.accessToken}`,
                                    },
                                });
                                return next.handle(retryRequest);
                            }),
                            catchError((err) => {
                                if (!err.ok) {
                                    this.commonService.showPopup();
                                }
                                return throwError(() => err);
                            })
                        );
                    } else if (error.status === 403) {
                        this.authService.logOut();
                    } else if (error.status === HttpStatusCode.PaymentRequired) {
                        // Out of credits (TOKEN_EXHAUSTED / TOKEN_INSUFFICIENT / TOKEN_RESERVATION_FAILED).
                        // Shown once here for every AI caller; the error is still rethrown so callers can stop.
                        this.toasterService.showWarning(
                            error.error?.message ||
                                this.translationService.getLabels('walletLabels').exhaustedMessage
                        );
                        this.walletService.refreshWallet();
                    }
                } else if (
                    request.url.includes('/auth/verifyOTP') &&
                    error.status === 401
                ) {
                    this.authService.logOut();
                }
                return throwError(() => error);
            }),
            tap((event) => {
                if (event instanceof HttpResponse && this.isAiRequest(request)) {
                    this.walletService.refreshWallet();
                }
            })
        );
    }
}
